import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import $ from "jquery";
import imgLocation from "../controllers/imagePath";
import ApiURl from "../controllers/Api";
import { ShoppingCart } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="600" height="600">
      <rect width="100%" height="100%" fill="#f8efe6"/>
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
        font-family="Arial" font-size="24" fill="#8b746f">No Image</text>
    </svg>
  `);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const IconHeart = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
  </svg>
);

const IconGrid = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" {...props}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const IconList = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" {...props}>
    <path d="M9 6h12M9 12h12M9 18h12" />
    <path d="M4 6h.01M4 12h.01M4 18h.01" />
  </svg>
);

const IconSliders = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M4 7h9M17 7h3M4 17h3M11 17h9" />
    <circle cx="15" cy="7" r="2" />
    <circle cx="9" cy="17" r="2" />
  </svg>
);

const IconChevronDown = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const IconChevronRight = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="m9 6 6 6-6 6" />
  </svg>
);

const IconClose = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const PRODUCT_BANNER =
  "https://images.unsplash.com/photo-1617038220319-276d3cfab638?q=85&w=2200&auto=format&fit=crop";

const SORT_OPTIONS = [
  "Featured",
  "A-Z",
  "Z-A",
  "Price: Low to High",
  "Price: High to Low",
  "Newest First",
];

const getProductSpec = (product) => {
  const purityFromOptions = Array.isArray(product?.purity_options)
    ? product.purity_options
        .map((item) => item?.purity_name || item?.name || "")
        .filter(Boolean)
        .join(", ")
    : "";
  const purity = String(
    product?.purity || product?.purity_name || product?.purities || purityFromOptions || ""
  ).trim();

  if (purity) {
    return { label: "Purity", value: purity };
  }

  const size = String(product?.sizes || product?.product_size || product?.size || "").trim();
  return { label: "Size", value: size || "N/A" };
};

const PAGE_STYLE = `
  .slp-root,
  .slp-root * { box-sizing: border-box; }
  .slp-root {
    --slp-bg: var(--user-bg, #E8D9D5);
    --slp-paper: var(--user-card, #fffaf5);
    --slp-card: var(--user-card, #fffaf6);
    --slp-maroon: var(--user-maroon, #2B0000);
    --slp-maroon-deep: var(--user-maroon-deep, #170000);
    --slp-gold: var(--user-gold, #c99b3f);
    --slp-gold-soft: var(--user-gold-soft, #e7bd67);
    --slp-ink: var(--user-text, #301313);
    --slp-muted: var(--user-text-soft, #715c56);
    --slp-line: var(--user-border, #e5d2c7);
    min-height: 100vh;
    background: var(--slp-bg);
    color: var(--slp-ink);
    font-family: Kanit, Arial, sans-serif;
  }
  .slp-serif { font-family: Georgia, "Times New Roman", serif; }
  .slp-page-shell { width: 100%; }
  .slp-hero {
    position: relative;
    min-height: 115px;
    overflow: hidden;
    background: var(--slp-maroon-deep);
  }
  .slp-hero-image { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; object-position:center 46%; }
  .slp-hero-overlay { position:absolute; inset:0; background:linear-gradient(90deg,rgba(28,0,0,.98) 0%,rgba(35,0,0,.86) 34%,rgba(35,0,0,.24) 68%,rgba(35,0,0,.08) 100%),linear-gradient(0deg,rgba(20,0,0,.36),transparent 55%); }
  .slp-hero-inner { position:relative; z-index:2; width:min(1500px,calc(100% - 72px)); margin:0 auto; padding:18px 0 16px; }
  .slp-hero h1 { margin:0; color:var(--slp-gold-soft); font-size:clamp(34px,3.6vw,54px); font-weight:400; line-height:1; }
  .slp-title-rule { display:flex; align-items:center; gap:10px; width:118px; margin:10px 0 0; color:var(--slp-gold); }
  .slp-title-rule span { height:1px; flex:1; background:currentColor; }
  .slp-title-rule i { width:9px; height:9px; border:1px solid currentColor; transform:rotate(45deg); }
  .slp-breadcrumb { display:flex; align-items:center; gap:10px; color:#fff7ef; font-size:15px; }
  .slp-breadcrumb a { color:inherit; text-decoration:none; }
  .slp-main { width:min(1500px,calc(100% - 72px)); margin:0 auto; padding:28px 0 54px; }
  .slp-layout { display:grid; grid-template-columns:270px minmax(0,1fr); gap:28px; align-items:start; }
  .slp-filter-panel { position:sticky; top:90px; border-right:1px solid var(--slp-line); padding-right:26px; }
  .slp-filter-head { display:flex; align-items:center; justify-content:space-between; padding:8px 0 17px; border-bottom:1px solid var(--slp-line); }
  .slp-filter-head h2 { margin:0; font-size:18px; font-weight:500; }
  .slp-filter-reset { border:0; background:none; color:var(--slp-muted); cursor:pointer; padding:4px; }
  .slp-filter-section { padding:18px 0; border-bottom:1px solid var(--slp-line); }
  .slp-filter-section:last-child { border-bottom:0; }
  .slp-filter-title { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; font-size:13px; font-weight:600; letter-spacing:.04em; text-transform:uppercase; }
  .slp-filter-title svg { width:15px; height:15px; }
  .slp-check-list { display:flex; flex-direction:column; gap:10px; }
  .slp-check-row { display:flex; align-items:center; gap:10px; cursor:pointer; font-size:14px; }
  .slp-check-row input { appearance:none; width:18px; height:18px; border:1px solid #d9bfae; border-radius:3px; background:#fff; position:relative; flex:none; }
  .slp-check-row input:checked { background:var(--slp-maroon); border-color:var(--slp-maroon); }
  .slp-check-row input:checked::after { content:""; position:absolute; left:5px; top:2px; width:5px; height:9px; border:solid #fff; border-width:0 2px 2px 0; transform:rotate(45deg); }
  .slp-check-row input:disabled { opacity:.45; cursor:not-allowed; }
  .slp-count { color:var(--slp-muted); }
  .slp-range-wrap { padding-top:10px; }
  .slp-range-track { position:relative; height:4px; background:#ead8cd; border-radius:99px; }
  .slp-range-fill { position:absolute; top:0; height:4px; border-radius:99px; background:var(--slp-maroon); }
  .slp-range-thumb { position:absolute; top:50%; width:16px; height:16px; border:1px solid var(--slp-maroon); border-radius:50%; background:var(--slp-gold-soft); transform:translate(-50%,-50%); pointer-events:none; }
  .slp-range-input { position:absolute; inset:-13px 0 auto; width:100%; height:30px; opacity:0; cursor:pointer; }
  .slp-price-inputs { display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:24px; }
  .slp-price-box { position:relative; }
  .slp-price-box span { position:absolute; left:13px; top:50%; transform:translateY(-50%); font-size:13px; }
  .slp-price-box input { width:100%; height:42px; border:1px solid var(--slp-line); border-radius:7px; background:#fffaf6; padding:0 10px 0 28px; color:var(--slp-ink); outline:none; }
  .slp-price-box input:focus { border-color:var(--slp-gold); }
  .slp-apply-btn { width:100%; height:47px; margin-top:18px; border:0; border-radius:7px; background:linear-gradient(90deg,var(--slp-maroon),var(--slp-maroon-deep),var(--slp-maroon)); color:var(--user-cream, white); font-size:14px; font-weight:600; cursor:pointer; box-shadow:0 10px 20px rgba(43,0,0,.15); }
  .slp-clear-link { display:block; margin:13px auto 0; border:0; background:none; color:#9a7161; cursor:pointer; font-size:13px; }
  .slp-content { min-width:0; }
  .slp-toolbar { display:flex; align-items:center; justify-content:space-between; gap:18px; margin-bottom:18px; }
  .slp-result-count { font-size:14px; color:#3d2723; }
  .slp-toolbar-right { display:flex; align-items:center; gap:12px; }
  .slp-sort-label { font-size:13px; }
  .slp-sort { height:42px; min-width:150px; border:1px solid var(--slp-line); border-radius:7px; background:#fffaf6; color:var(--slp-ink); padding:0 38px 0 14px; outline:none; }
  .slp-view-button { display:grid; place-items:center; width:42px; height:42px; border:1px solid var(--slp-line); border-radius:7px; background:#fffaf6; color:var(--slp-maroon); cursor:pointer; }
  .slp-view-button.active { background:var(--slp-maroon); color:#fff; border-color:var(--slp-maroon); }
  .slp-view-button svg { width:19px; height:19px; }
  .slp-mobile-toolbar { display:none; }
  .slp-products-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:18px; }
  .slp-product-card { position:relative; display:flex; flex-direction:column; min-width:0; overflow:hidden; border:1px solid var(--slp-line); border-radius:10px; background:rgba(255,250,246,.84); box-shadow:0 5px 18px rgba(74,28,20,.04); transition:border-color .3s ease,box-shadow .3s ease; }
  .slp-product-card:hover { border-color:rgba(201,155,63,.8); box-shadow:0 18px 40px rgba(62,17,11,.12); }
  .slp-card-image-link { position:relative; display:block; overflow:hidden; background:#f9f0e7; }
  .slp-card-image { position:relative; aspect-ratio:1/1; overflow:hidden; }
  .slp-card-image img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; transition:opacity .35s ease,transform .75s cubic-bezier(.22,1,.36,1); }
  .slp-product-card:hover .slp-primary { transform:scale(1.06); }
  .slp-secondary { opacity:0; }
  .slp-product-card:hover .slp-primary.has-secondary { opacity:0; }
  .slp-product-card:hover .slp-secondary { opacity:1; transform:scale(1.05); }
  .slp-badge { position:absolute; z-index:5; left:11px; top:11px; padding:6px 10px; border-radius:5px; background:#f3ce84; color:#341400; font-size:11px; font-weight:700; text-transform:uppercase; box-shadow:0 4px 12px rgba(0,0,0,.08); }
  .slp-wish { position:absolute; z-index:5; right:11px; top:11px; display:grid; place-items:center; width:34px; height:34px; border:1px solid rgba(43,0,0,.09); border-radius:50%; background:rgba(255,255,255,.93); color:var(--slp-maroon); }
  .slp-wish svg { width:18px; height:18px; }
  .slp-card-body { display:flex; flex:1; flex-direction:column; padding:12px 14px 13px; }
  .slp-card-title { margin:0; color:var(--slp-ink); font-size:18px; font-weight:500; line-height:1.2; text-decoration:none; }
  .slp-card-desc { margin:5px 0 0; color:#684d47; font-size:12px; line-height:1.45; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
  .slp-card-size { margin:6px 0 0; color:#8a716b; font-size:12px; }
  .slp-card-price { margin-top:5px; min-height:24px; color:var(--slp-ink); }
  .slp-price-row { display:flex; align-items:baseline; gap:7px; }
  .slp-price-final { font-size:16px; font-weight:700; }
  .slp-price-original { font-size:11px; color:#947e79; text-decoration:line-through; }
  .slp-card-actions { display:grid; grid-template-columns:1fr 48px; gap:10px; margin-top:auto; padding-top:10px; }
  .slp-details-btn,.slp-bag-btn { height:38px; border:1px solid var(--slp-line); border-radius:7px; background:#f5e9de; color:var(--slp-maroon); display:flex; align-items:center; justify-content:center; text-decoration:none; font-size:12px; transition:.25s ease; }
  .slp-bag-btn { background:#fffaf6; }
  .slp-details-btn:hover,.slp-bag-btn:hover { background:var(--slp-maroon); border-color:var(--slp-maroon); color:white; }
  .slp-bag-btn svg { width:19px; height:19px; }
  .slp-list-mode { grid-template-columns:1fr; }
  .slp-list-mode .slp-product-card { display:grid; grid-template-columns:250px minmax(0,1fr); }
  .slp-list-mode .slp-card-image { height:100%; min-height:230px; aspect-ratio:auto; }
  .slp-list-mode .slp-card-body { padding:22px; }
  .slp-loading-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:18px; }
  .slp-skeleton { overflow:hidden; border:1px solid var(--slp-line); border-radius:10px; background:#fff; }
  .slp-skeleton-img,.slp-skeleton-line { position:relative; overflow:hidden; background:#f1e5dc; }
  .slp-skeleton-img::after,.slp-skeleton-line::after { content:""; position:absolute; inset:0; transform:translateX(-100%); background:linear-gradient(90deg,transparent,rgba(255,255,255,.8),transparent); animation:slpShimmer 1.3s infinite; }
  .slp-skeleton-img { aspect-ratio:1/1; }
  .slp-skeleton-body { padding:14px; }
  .slp-skeleton-line { height:12px; border-radius:4px; margin-bottom:10px; }
  .slp-empty { padding:60px 20px; border:1px solid var(--slp-line); border-radius:12px; background:#fffaf6; text-align:center; }
  .slp-empty h3 { margin:0 0 7px; font-size:26px; font-weight:500; }
  .slp-empty p { margin:0; color:var(--slp-muted); }
  .slp-mobile-filter-overlay { display:none; }
  @keyframes slpShimmer { to { transform:translateX(100%); } }
  @media (max-width:1250px) {
    .slp-products-grid,.slp-loading-grid { grid-template-columns:repeat(3,minmax(0,1fr)); }
  }
  @media (max-width:980px) {
    .slp-main,.slp-hero-inner { width:min(100% - 34px,900px); }
    .slp-layout { grid-template-columns:230px minmax(0,1fr); gap:20px; }
    .slp-products-grid,.slp-loading-grid { grid-template-columns:repeat(2,minmax(0,1fr)); }
  }
  @media (max-width:767px) {
    .slp-hero { min-height:78px; }
    .slp-hero-inner { width:calc(100% - 34px); padding:16px 0 12px; }
    .slp-hero h1 { font-size:30px; }
    .slp-title-rule { margin:8px 0 0; width:105px; }
    .slp-breadcrumb { font-size:12px; }
    .slp-main { width:100%; padding:18px 14px 36px; }
    .slp-layout { display:block; }
    .slp-filter-panel,.slp-toolbar { display:none; }
    .slp-mobile-toolbar { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:17px; }
    .slp-mobile-left { display:flex; align-items:center; gap:13px; min-width:0; }
    .slp-filter-open { display:inline-flex; align-items:center; gap:7px; height:41px; border:1px solid var(--slp-line); border-radius:7px; background:#fffaf6; color:var(--slp-ink); padding:0 13px; font-family:Georgia,"Times New Roman",serif; font-size:15px; font-weight:600; cursor:pointer; }
    .slp-filter-open svg { width:17px; height:17px; }
    .slp-mobile-count { font-size:11px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .slp-mobile-views { display:flex; gap:8px; }
    .slp-view-button { width:40px; height:40px; }
    .slp-products-grid,.slp-loading-grid { grid-template-columns:repeat(2,minmax(0,1fr)); gap:12px; }
    .slp-products-grid.mobile-list { grid-template-columns:1fr; gap:16px; }
    .slp-products-grid.mobile-list .slp-product-card { display:grid; grid-template-columns:47% minmax(0,1fr); min-height:228px; }
    .slp-products-grid.mobile-list .slp-card-image-link,.slp-products-grid.mobile-list .slp-card-image { height:100%; min-height:228px; aspect-ratio:auto; }
    .slp-products-grid.mobile-list .slp-card-body { padding:14px 12px 11px; }
    .slp-products-grid.mobile-list .slp-card-title { font-size:18px; line-height:1.35; }
    .slp-products-grid.mobile-list .slp-card-desc { font-size:12px; -webkit-line-clamp:3; margin-top:8px; }
    .slp-products-grid.mobile-list .slp-card-size { margin-top:8px; }
    .slp-products-grid.mobile-list .slp-card-price { margin-top:5px; }
    .slp-products-grid.mobile-list .slp-card-actions { grid-template-columns:1fr 46px; }
    .slp-card-title { font-size:15px; }
    .slp-card-desc { display:none; }
    .slp-card-body { padding:10px; }
    .slp-card-actions { grid-template-columns:1fr 42px; gap:7px; }
    .slp-details-btn,.slp-bag-btn { height:35px; }
    .slp-mobile-filter-overlay { position:fixed; inset:0; z-index:9999; display:block; opacity:0; pointer-events:none; background:rgba(20,0,0,.35); transition:opacity .3s ease; }
    .slp-mobile-filter-overlay.open { opacity:1; pointer-events:auto; }
    .slp-mobile-filter-sheet { position:absolute; left:0; right:0; top:0; max-height:100%; overflow:auto; background:#fffaf6; transform:translateY(-25px); opacity:0; transition:transform .36s cubic-bezier(.22,1,.36,1),opacity .3s ease; box-shadow:0 18px 50px rgba(43,0,0,.24); }
    .slp-mobile-filter-overlay.open .slp-mobile-filter-sheet { transform:translateY(0); opacity:1; }
    .slp-mobile-filter-head { display:flex; align-items:center; justify-content:space-between; padding:18px 20px; border-bottom:1px solid var(--slp-line); }
    .slp-mobile-filter-head h2 { margin:0; font-family:Georgia,"Times New Roman",serif; font-size:19px; font-weight:500; }
    .slp-mobile-filter-head-actions { display:flex; align-items:center; gap:16px; }
    .slp-mobile-clear { border:0; background:none; color:#c31d25; font-size:14px; cursor:pointer; }
    .slp-mobile-close { display:grid; place-items:center; width:35px; height:35px; border:0; border-radius:7px; background:#fff5ec; color:var(--slp-maroon); }
    .slp-mobile-filter-content { padding:0 20px 18px; }
    .slp-mobile-filter-content .slp-filter-section { padding:17px 0; }
    .slp-mobile-filter-content .slp-check-row { font-size:13px; }
    .slp-mobile-apply { position:sticky; bottom:0; padding:12px 20px 15px; background:linear-gradient(0deg,#fffaf6 75%,rgba(255,250,246,0)); }
    .slp-mobile-apply .slp-apply-btn { margin:0; }
  }
  @media (max-width:430px) {
    .slp-main { padding-inline:10px; }
    .slp-products-grid.mobile-list .slp-product-card { grid-template-columns:46% minmax(0,1fr); min-height:210px; }
    .slp-products-grid.mobile-list .slp-card-image-link,.slp-products-grid.mobile-list .slp-card-image { min-height:210px; }
    .slp-products-grid.mobile-list .slp-card-title { font-size:16px; }
    .slp-products-grid.mobile-list .slp-card-desc { font-size:11px; }
    .slp-products-grid.mobile-list .slp-card-actions { grid-template-columns:minmax(0,1fr) 40px; }
    .slp-products-grid.mobile-list .slp-details-btn { font-size:11px; }
  }
  @media (prefers-reduced-motion:reduce) {
    .slp-product-reveal,.slp-hero-reveal { opacity:1!important; transform:none!important; }
  }
`;

function PriceRange({ priceStats, priceRange, priceDraft, onSliderChange, onInputFocus, onInputChange, onInputCommit }) {
  const min = priceStats.min;
  const max = Math.max(priceStats.max, min + 1);
  const range = max - min;
  const minPct = ((priceRange[0] - min) / range) * 100;
  const maxPct = ((priceRange[1] - min) / range) * 100;

  return (
    <div className="slp-range-wrap">
      <div className="slp-range-track">
        <div className="slp-range-fill" style={{ left: `${minPct}%`, right: `${100 - maxPct}%` }} />
        <div className="slp-range-thumb" style={{ left: `${minPct}%` }} />
        <div className="slp-range-thumb" style={{ left: `${maxPct}%` }} />
        <input className="slp-range-input" type="range" min={min} max={max} step="100" value={priceRange[0]} onChange={(e) => onSliderChange(0, e.target.value)} style={{ zIndex: 3 }} />
        <input className="slp-range-input" type="range" min={min} max={max} step="100" value={priceRange[1]} onChange={(e) => onSliderChange(1, e.target.value)} style={{ zIndex: 2 }} />
      </div>

      <div className="slp-price-inputs">
        {[0, 1].map((index) => (
          <div className="slp-price-box" key={index}>
            <span>₹</span>
            <input
              type="text"
              inputMode="numeric"
              value={priceDraft[index]}
              onFocus={() => onInputFocus(index)}
              onChange={(e) => onInputChange(index, e.target.value)}
              onBlur={() => onInputCommit(index)}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterContent({
  fetchedCategories,
  selectedCategories,
  toggleCategory,
  getProductCountForCategory,
  priceStats,
  priceRange,
  priceDraft,
  onPriceSliderChange,
  onPriceInputFocus,
  onPriceInputChange,
  onPriceInputCommit,
}) {
  const allSelected = fetchedCategories.length > 0 && selectedCategories.length === fetchedCategories.length;

  return (
    <>
      <div className="slp-filter-section">
        <div className="slp-filter-title"><span>Categories</span><IconChevronDown /></div>
        <div className="slp-check-list">
          <label className="slp-check-row">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={() => {
                if (allSelected) {
                  fetchedCategories.forEach((cat) => {
                    if (selectedCategories.includes(cat.id.toString())) toggleCategory(cat.id.toString());
                  });
                } else {
                  fetchedCategories.forEach((cat) => {
                    if (!selectedCategories.includes(cat.id.toString())) toggleCategory(cat.id.toString());
                  });
                }
              }}
            />
            <span>All Categories</span>
          </label>
          {fetchedCategories.map((cat) => {
            const id = cat.id.toString();
            const count = getProductCountForCategory[id] || 0;
            return (
              <label className="slp-check-row" key={id}>
                <input type="checkbox" checked={selectedCategories.includes(id)} onChange={() => toggleCategory(id)} disabled={count === 0} />
                <span>{cat.category_name} <span className="slp-count">({count})</span></span>
              </label>
            );
          })}
        </div>
      </div>

      <div className="slp-filter-section">
        <div className="slp-filter-title"><span>Price Range</span><IconChevronDown /></div>
        <PriceRange
          priceStats={priceStats}
          priceRange={priceRange}
          priceDraft={priceDraft}
          onSliderChange={onPriceSliderChange}
          onInputFocus={onPriceInputFocus}
          onInputChange={onPriceInputChange}
          onInputCommit={onPriceInputCommit}
        />
      </div>
    </>
  );
}

function ProductCard({ product, index, getProductImages, getEffectivePrice, getDescriptionSummary, viewMode }) {
  const { primary, secondary } = getProductImages(product);
  const price = getEffectivePrice(product);
  const spec = getProductSpec(product);
  const detailsPath = `/ProductOverview/${product.original_product_id || product.product_id}`;
  const badge = price.hasDiscount && price.percent > 0 ? `${price.percent}% OFF` : index % 7 === 0 ? "New" : index % 5 === 0 ? "Bestseller" : null;

  return (
    <article className="slp-product-card slp-product-reveal">
      <Link to={detailsPath} className="slp-card-image-link" aria-label={`View ${product.product_name}`}>
        <div className="slp-card-image">
          {badge && <span className="slp-badge">{badge}</span>}
          <span className="slp-wish" aria-hidden="true"><IconHeart /></span>
          <img
            src={primary}
            alt={product.product_name || "Jewellery product"}
            loading="lazy"
            className={`slp-primary ${secondary ? "has-secondary" : ""}`}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMG; }}
          />
          {secondary && (
            <img
              src={secondary}
              alt={`${product.product_name || "Product"} alternate`}
              loading="lazy"
              className="slp-secondary"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = primary || FALLBACK_IMG; }}
            />
          )}
        </div>
      </Link>

      <div className="slp-card-body">
        <Link to={detailsPath} className="slp-card-title slp-serif">{product.product_name || product.name || "Jewellery Product"}</Link>
        <p className="slp-card-desc">{getDescriptionSummary(product.description, viewMode === "list" ? 24 : 14)}</p>
        <p className="slp-card-size">{spec.label}: {spec.value}</p>
        <div className="slp-card-price">
          {price.final > 0 ? (
            <div className="slp-price-row">
              <span className="slp-price-final">₹{Math.round(price.final).toLocaleString("en-IN")}</span>
              {price.hasDiscount && price.original > price.final && (
                <span className="slp-price-original">₹{Math.round(price.original).toLocaleString("en-IN")}</span>
              )}
            </div>
          ) : (
            <span className="slp-price-final">Price on request</span>
          )}
        </div>
        <div className="slp-card-actions">
          <Link to={detailsPath} className="slp-details-btn">View Details</Link>
          <Link to={detailsPath} className="slp-bag-btn" aria-label={`View ${product.product_name}`}><ShoppingCart /></Link>
        </div>
      </div>
    </article>
  );
}

export default function AllProducts() {
  const rootRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState("Featured");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [priceDraft, setPriceDraft] = useState(["0", "10000"]);
  const [editingPriceIndex, setEditingPriceIndex] = useState(null);
  const [fetchedCategories, setFetchedCategories] = useState([]);
  const [viewMode, setViewMode] = useState("grid");

  const toImgUrl = (value) => {
    if (!value || typeof value !== "string") return null;
    const normalized = value.trim();
    if (!normalized) return null;
    if (/^https?:\/\//i.test(normalized)) return normalized;
    return `${imgLocation}/${normalized}`;
  };

  const getProductImages = (product) => {
    const raw = product?.img_path;
    if (!raw) return { primary: FALLBACK_IMG, secondary: null };
    if (typeof raw === "string" && /^https?:\/\//i.test(raw)) return { primary: raw, secondary: null };
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          const primary = toImgUrl(parsed[0]) || FALLBACK_IMG;
          const secondary = toImgUrl(parsed[1]);
          return { primary, secondary: secondary && secondary !== primary ? secondary : null };
        }
      } catch {}
      const separator = trimmed.includes(",") ? "," : trimmed.includes("|") ? "|" : null;
      if (separator) {
        const parts = trimmed.split(separator).map((item) => item.trim()).filter(Boolean);
        const primary = toImgUrl(parts[0]) || FALLBACK_IMG;
        const secondary = toImgUrl(parts[1]);
        return { primary, secondary: secondary && secondary !== primary ? secondary : null };
      }
      return { primary: toImgUrl(trimmed) || FALLBACK_IMG, secondary: null };
    }
    return { primary: FALLBACK_IMG, secondary: null };
  };

  const getEffectivePrice = (product) => {
    if (!product) return { final: 0, original: 0, hasDiscount: false, percent: 0 };
    if (product.has_discount && product.discounted_price && product.original_price) {
      const final = Number(product.discounted_price);
      const original = Number(product.original_price);
      return { final, original, hasDiscount: true, percent: Number(product.discount_percent) || Math.round(((original - final) / original) * 100) };
    }
    if (Array.isArray(product.prices) && product.prices.length) {
      let best = { final: Infinity, original: Infinity, hasDiscount: false, percent: 0 };
      product.prices.forEach((item) => {
        const original = Number(item.price || 0);
        if (!original) return;
        let final = original;
        const type = item.single_discount_type;
        const value = Number(item.single_discount_value || 0);
        if (type === "percentage") final = original * (1 - value / 100);
        if (type === "amount" || type === "fixed") final = original - value;
        if (final < best.final) best = { final, original, hasDiscount: final < original, percent: final < original ? Math.round(((original - final) / original) * 100) : 0 };
      });
      if (Number.isFinite(best.final)) return best;
    }
    if (product.prices_string) {
      const prices = String(product.prices_string).split(",").map(Number).filter((value) => Number.isFinite(value) && value > 0);
      const types = String(product.single_discount_types || "").split(",");
      const values = String(product.single_discount_values || "").split(",").map(Number);
      let best = { final: Infinity, original: Infinity, hasDiscount: false, percent: 0 };
      prices.forEach((original, index) => {
        let final = original;
        if (types[index] === "percentage") final = original * (1 - (values[index] || 0) / 100);
        if (types[index] === "amount" || types[index] === "fixed") final = original - (values[index] || 0);
        if (final < best.final) best = { final, original, hasDiscount: final < original, percent: final < original ? Math.round(((original - final) / original) * 100) : 0 };
      });
      if (Number.isFinite(best.final)) return best;
    }
    const original = Number(product.min_price || product.price || 0);
    const type = product.single_discount_types;
    const value = Number(product.single_discount_values || 0);
    if (original > 0 && value > 0 && type) {
      let final = original;
      if (type === "percentage") final = original * (1 - value / 100);
      if (type === "amount" || type === "fixed") final = original - value;
      if (final < original) return { final, original, hasDiscount: true, percent: Math.round(((original - final) / original) * 100) };
    }
    return { final: original, original, hasDiscount: false, percent: 0 };
  };

  const getDescriptionSummary = (text, maxWords = 16) => {
    const plain = String(text || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    if (!plain) return "Elegant craftsmanship created for timeless occasions.";
    const words = plain.split(" ");
    return words.length > maxWords ? `${words.slice(0, maxWords).join(" ")}…` : plain;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await $.getJSON(`${ApiURl}/getMainCategory.php`);
        const active = Array.isArray(response?.data) ? response.data.filter((item) => item.is_active === "1") : [];
        setFetchedCategories(active);
        setSelectedCategories(active.map((item) => item.id.toString()));
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await $.ajax({ url: `${ApiURl}/getallproducts.php`, method: "GET", dataType: "json", timeout: 10000 });
        let productList = [];
        if (response?.status === "success" && Array.isArray(response.products)) productList = response.products;
        else if (Array.isArray(response)) productList = response;
        else if (Array.isArray(response?.data)) productList = response.data;

        const processed = productList.map((product) => {
          if (!product?.product_id) return null;
          const categoryName = String(product.category_name || "Uncategorized").trim().toLowerCase().split(" ").filter(Boolean).map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
          return {
            product_id: String(product.product_id),
            original_product_id: product.product_id,
            product_name: product.product_name || product.name || "Jewellery Product",
            name: product.product_name || product.name || "Jewellery Product",
            category_id: String(product.category_id || product.main_category_id || "").trim(),
            category_name: categoryName,
            description: product.description || "",
            img_path: product.img_path || product.image || "",
            prices: product.prices || [],
            prices_string: product.prices_string || "",
            single_discount_types: product.single_discount_types || "",
            single_discount_values: product.single_discount_values || "",
            has_discount: product.has_discount || false,
            discounted_price: product.discounted_price,
            original_price: product.original_price,
            discount_percent: product.discount_percent || 0,
            min_price: product.min_price || 0,
            price: product.product_price || product.price || 0,
            sizes: product.product_size || product.sizes || "",
            purity: product.purity || product.purity_name || product.purities || "",
            purity_options: Array.isArray(product.purity_options) ? product.purity_options : [],
            has_purity_pricing: Boolean(product.has_purity_pricing),
            created_at: product.created_at || new Date().toISOString(),
          };
        }).filter(Boolean);

        setProducts(processed);
        const prices = processed.map((item) => getEffectivePrice(item).final).filter((value) => value > 0);
        const min = prices.length ? Math.floor(Math.min(...prices)) : 0;
        const max = prices.length ? Math.ceil(Math.max(...prices)) : 10000;
        setPriceRange([min, max]);
        setPriceDraft([String(min), String(max)]);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError(`Error loading products: ${err.message}`);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const priceStats = useMemo(() => {
    const prices = products.map((item) => getEffectivePrice(item).final).filter((value) => value > 0);
    if (!prices.length) return { min: 0, max: 10000 };
    return { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) };
  }, [products]);

  useEffect(() => {
    if (editingPriceIndex === null) setPriceDraft([String(priceRange[0]), String(priceRange[1])]);
  }, [priceRange, editingPriceIndex]);

  useEffect(() => {
    document.body.style.overflow = filtersOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [filtersOpen]);

  const getProductCountForCategory = useMemo(() => {
    const result = {};
    fetchedCategories.forEach((cat) => {
      const id = cat.id.toString();
      result[id] = products.filter((product) => product.category_id === id || product.category_name.toLowerCase() === String(cat.category_name || "").toLowerCase()).length;
    });
    return result;
  }, [products, fetchedCategories]);

  const toggleCategory = (id) => {
    setSelectedCategories((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const clearFilters = () => {
    setSort("Featured");
    setSelectedCategories(fetchedCategories.map((item) => item.id.toString()));
    setPriceRange([priceStats.min, priceStats.max]);
  };

  const handlePriceSliderChange = (index, raw) => {
    const value = Number(raw);
    setPriceRange((current) => {
      const next = [...current];
      next[index] = clamp(value, priceStats.min, priceStats.max);
      if (index === 0 && next[0] > next[1]) next[1] = next[0];
      if (index === 1 && next[1] < next[0]) next[0] = next[1];
      return next;
    });
  };

  const handlePriceInputChange = (index, raw) => {
    const cleaned = String(raw).replace(/\D/g, "");
    setPriceDraft((current) => current.map((value, i) => i === index ? cleaned : value));
    const numeric = Number(cleaned);
    if (cleaned !== "" && Number.isFinite(numeric)) handlePriceSliderChange(index, numeric);
  };

  const commitPriceInput = (index) => {
    if (!priceDraft[index]) setPriceDraft((current) => current.map((value, i) => i === index ? String(priceRange[index]) : value));
    setEditingPriceIndex(null);
  };

  const filteredProducts = useMemo(() => {
    let list = [...products];
    if (selectedCategories.length && selectedCategories.length < fetchedCategories.length) {
      list = list.filter((product) => selectedCategories.some((selectedId) => {
        const selected = fetchedCategories.find((item) => item.id.toString() === selectedId);
        return product.category_id === selectedId || product.category_name.toLowerCase() === String(selected?.category_name || "").toLowerCase();
      }));
    }
    list = list.filter((product) => {
      const price = getEffectivePrice(product).final;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    if (sort === "A-Z") list.sort((a, b) => a.product_name.localeCompare(b.product_name));
    if (sort === "Z-A") list.sort((a, b) => b.product_name.localeCompare(a.product_name));
    if (sort === "Price: Low to High") list.sort((a, b) => getEffectivePrice(a).final - getEffectivePrice(b).final);
    if (sort === "Price: High to Low") list.sort((a, b) => getEffectivePrice(b).final - getEffectivePrice(a).final);
    if (sort === "Newest First") list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list;
  }, [products, selectedCategories, fetchedCategories, priceRange, sort]);

  useEffect(() => {
    if (!rootRef.current || loading) return;
    const context = gsap.context(() => {
      gsap.fromTo(".slp-hero-reveal", { opacity: 0, y: 35 }, { opacity: 1, y: 0, duration: 1, stagger: 0.12, ease: "power3.out" });
      ScrollTrigger.batch(".slp-product-reveal", {
        start: "top 90%",
        onEnter: (batch) => gsap.fromTo(batch, { opacity: 0, y: 42, scale: 0.97 }, { opacity: 1, y: 0, scale: 1, duration: 0.72, stagger: 0.08, ease: "power3.out", overwrite: true }),
        onLeaveBack: (batch) => gsap.to(batch, { opacity: 0, y: 24, duration: 0.35, overwrite: true }),
      });
      gsap.to(".slp-hero-image", { yPercent: 10, ease: "none", scrollTrigger: { trigger: ".slp-hero", start: "top top", end: "bottom top", scrub: 1 } });
    }, rootRef);
    const timer = setTimeout(() => ScrollTrigger.refresh(), 300);
    return () => { clearTimeout(timer); context.revert(); };
  }, [loading, filteredProducts.length, viewMode]);

  if (error) {
    return (
      <main className="slp-root" style={{ padding: "80px 20px", textAlign: "center" }}>
        <style>{PAGE_STYLE}</style>
        <h2 className="slp-serif">Connection error</h2>
        <p>{error}</p>
        <button className="slp-apply-btn" style={{ maxWidth: 260 }} onClick={() => window.location.reload()}>Retry Loading</button>
      </main>
    );
  }

  return (
    <main ref={rootRef} className="slp-root">
      <style>{PAGE_STYLE}</style>

      <section className="slp-hero">
        <img className="slp-hero-image" src={PRODUCT_BANNER} alt="Gold jewellery collection" />
        <div className="slp-hero-overlay" />
        <div className="slp-hero-inner">
          <h1 className="slp-serif slp-hero-reveal">Our Collection</h1>
          <div className="slp-title-rule slp-hero-reveal"><span /><i /><span /></div>
        </div>
      </section>

      <div className="slp-main">
        <div className="slp-mobile-toolbar">
          <div className="slp-mobile-left">
            <button className="slp-filter-open" onClick={() => setFiltersOpen(true)}><span>Filter</span><IconSliders /></button>
            <span className="slp-mobile-count">Showing 1–{filteredProducts.length} of {products.length} products</span>
          </div>
          <div className="slp-mobile-views">
            <button className={`slp-view-button ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")} aria-label="Grid view"><IconGrid /></button>
            <button className={`slp-view-button ${viewMode === "list" ? "active" : ""}`} onClick={() => setViewMode("list")} aria-label="List view"><IconList /></button>
          </div>
        </div>

        <div className="slp-layout">
          <aside className="slp-filter-panel">
            <div className="slp-filter-head"><h2 className="slp-serif">Filter By</h2><button className="slp-filter-reset" onClick={clearFilters}><IconSliders style={{ width: 18 }} /></button></div>
            <FilterContent
              fetchedCategories={fetchedCategories}
              selectedCategories={selectedCategories}
              toggleCategory={toggleCategory}
              getProductCountForCategory={getProductCountForCategory}
              priceStats={priceStats}
              priceRange={priceRange}
              priceDraft={priceDraft}
              onPriceSliderChange={handlePriceSliderChange}
              onPriceInputFocus={setEditingPriceIndex}
              onPriceInputChange={handlePriceInputChange}
              onPriceInputCommit={commitPriceInput}
            />
            <button className="slp-apply-btn">Apply Filters</button>
            <button className="slp-clear-link" onClick={clearFilters}>Clear All</button>
          </aside>

          <section className="slp-content">
            <div className="slp-toolbar">
              <div className="slp-result-count">Showing 1–{filteredProducts.length} of {products.length} products</div>
              <div className="slp-toolbar-right">
                <span className="slp-sort-label">Sort by:</span>
                <select className="slp-sort" value={sort} onChange={(e) => setSort(e.target.value)}>{SORT_OPTIONS.map((option) => <option key={option}>{option}</option>)}</select>
                <button className={`slp-view-button ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")}><IconGrid /></button>
                <button className={`slp-view-button ${viewMode === "list" ? "active" : ""}`} onClick={() => setViewMode("list")}><IconList /></button>
              </div>
            </div>

            {loading ? (
              <div className="slp-loading-grid">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div className="slp-skeleton" key={index}><div className="slp-skeleton-img" /><div className="slp-skeleton-body"><div className="slp-skeleton-line" style={{ width: "75%" }} /><div className="slp-skeleton-line" style={{ width: "55%" }} /><div className="slp-skeleton-line" style={{ width: "35%" }} /></div></div>
                ))}
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="slp-empty"><h3 className="slp-serif">No products match your filters</h3><p>Try changing your category or price selection.</p><button className="slp-apply-btn" style={{ maxWidth: 240 }} onClick={clearFilters}>Clear Filters</button></div>
            ) : (
              <div className={`slp-products-grid ${viewMode === "list" ? "slp-list-mode mobile-list" : ""}`}>
                {filteredProducts.map((product, index) => (
                  <ProductCard
                    key={`${product.product_id}-${index}`}
                    product={product}
                    index={index}
                    getProductImages={getProductImages}
                    getEffectivePrice={getEffectivePrice}
                    getDescriptionSummary={getDescriptionSummary}
                    viewMode={viewMode}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      <div className={`slp-mobile-filter-overlay ${filtersOpen ? "open" : ""}`} onClick={() => setFiltersOpen(false)}>
        <aside className="slp-mobile-filter-sheet" onClick={(e) => e.stopPropagation()}>
          <div className="slp-mobile-filter-head">
            <h2>Filter By</h2>
            <div className="slp-mobile-filter-head-actions">
              <button className="slp-mobile-clear" onClick={clearFilters}>Clear All</button>
              <button className="slp-mobile-close" onClick={() => setFiltersOpen(false)} aria-label="Close filters"><IconClose style={{ width: 18 }} /></button>
            </div>
          </div>
          <div className="slp-mobile-filter-content">
            <FilterContent
              fetchedCategories={fetchedCategories}
              selectedCategories={selectedCategories}
              toggleCategory={toggleCategory}
              getProductCountForCategory={getProductCountForCategory}
              priceStats={priceStats}
              priceRange={priceRange}
              priceDraft={priceDraft}
              onPriceSliderChange={handlePriceSliderChange}
              onPriceInputFocus={setEditingPriceIndex}
              onPriceInputChange={handlePriceInputChange}
              onPriceInputCommit={commitPriceInput}
            />
          </div>
          <div className="slp-mobile-apply"><button className="slp-apply-btn" onClick={() => setFiltersOpen(false)}>Apply Filters</button></div>
        </aside>
      </div>
    </main>
  );
}
